import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  hubspotSetPath,
  hubspotTrackPageView,
  isHubSpotLoaded
} from './hubspot';

/**
 * Reports SPA navigations to the HubSpot pixel.
 *
 * We drive this from the router rather than letting HubSpot's script detect
 * navigation itself, for two reasons: its own detection misses `replaceState`
 * and same-path query changes, and it patches `history` alongside posthog-js
 * (which patches earlier, at `init()`) — so relying on both leaves the ordering
 * to whichever library loaded last.
 *
 * Mounted inside <Router> next to ScrollToTop, since `useLocation` needs router
 * context.
 */
const HubSpotPageViews: React.FC = () => {
  const { pathname, search } = useLocation();
  const isFirstRun = useRef(true);

  useEffect(() => {
    // Skip the mount run: `applyConsentToHubSpot` already sent the entry
    // pageview at the moment it injected the script. Without this guard every
    // cold load reports its landing page twice, inflating session counts.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    // Re-checked per navigation rather than read once, so a mid-session consent
    // withdrawal stops pageviews immediately. Doubles as the "pixel disabled"
    // check, since the script is never present when it is off.
    if (!isHubSpotLoaded()) return;

    hubspotSetPath(`${pathname}${search}`);
    hubspotTrackPageView();
  }, [pathname, search]);

  return null;
};

export default HubSpotPageViews;
